import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  MapPin,
  Droplets,
  StickyNote,
  Package,
  Wrench,
  Box,
  KeyRound,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Settings,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { navItems, NAV_GROUP_ORDER } from '../../config/navigation';
import { useAuthStore } from '../../stores/auth-store';
import type { UserRole } from '../../types/auth';
import type { NavGroup, NavItem } from '../../types/nav';

// ─────────────────────────────────────────────────────────────
// Sidebar — Operations Deck (Navigation Prototype, May 2026)
//
// Layout (top → bottom):
//   • Brand mark
//   • Grouped nav (Core · Organization · Equipment & Tools · Reference)
//     – mono uppercase caption above each group
//     – Wells lives in Core, promoted from its old 5th spot
//   • User block with avatar + role + Settings gear
//     – Settings opens a small menu: Change Password · Logout
//
// Colours come from CSS variables (tokens.css) so Tailwind nested-key
// resolution can't bite us.
// ─────────────────────────────────────────────────────────────

const iconMap: Record<string, React.ComponentType<{ size?: number; style?: CSSProperties }>> = {
  LayoutDashboard,
  Building2,
  Users,
  MapPin,
  Droplets,
  StickyNote,
  Package,
  Wrench,
  Box,
  KeyRound,
};

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onToggle: () => void;
  onMobileClose: () => void;
}

// CSS variable references — single source of truth for theme values
const C = {
  bg:           'var(--color-bg-dark)',
  text:         'var(--color-sidebar-text)',
  textActive:   'var(--color-sidebar-text-active)',
  hover:        'var(--color-sidebar-hover)',
  active:       'var(--color-sidebar-active-bg)',
  section:      'var(--color-sidebar-section)',
  divider:      'var(--color-sidebar-divider)',
  accentBright: 'var(--color-accent-bright)',
} as const;

// Geonomics logo — white-inverted PNG for dark sidebar background.
// Falls back to the gradient diamond mark when collapsed (mini sidebar).
function GeonomicsLogo({ collapsed }: { collapsed: boolean }) {
  if (collapsed) {
    return (
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 7,
          background: 'linear-gradient(135deg, #2aa3c4, #0b4a6f)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          flexShrink: 0,
        }}
      >
        <svg width={15} height={15} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
          <path d="M7 1.5L2 7l5 5.5L12 7z" />
          <path d="M4.5 7h5" />
        </svg>
      </div>
    );
  }
  return (
    <img
      src="/geonomic-logo-v2.png"
      alt="Geonomic Technologies"
      style={{
        height: 30,
        width: 'auto',
        display: 'block',
        filter: 'brightness(0) invert(1)',  // white tint for dark bg
        flexShrink: 0,
      }}
    />
  );
}

function userInitials(username?: string, role?: string): string {
  if (role) {
    const caps = role.replace(/[^A-Z]/g, '');
    if (caps.length >= 2) return caps.slice(0, 2);
  }
  return (username ?? '??').slice(0, 2).toUpperCase();
}

export function Sidebar({ collapsed, mobileOpen, onToggle, onMobileClose }: SidebarProps) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const role = user?.role as UserRole | undefined;

  const visible = navItems.filter((item) => (role ? item.roles.includes(role) : false));
  const initials = userInitials(user?.username, user?.role);
  const showLabels = !collapsed || mobileOpen;

  // Group visible items by their `group` field. Items with no group are skipped here.
  const grouped: Record<NavGroup, NavItem[]> = {
    Core: [],
    Organization: [],
    'Equipment & Tools': [],
    Reference: [],
  };
  visible.forEach((item) => {
    if (item.group) grouped[item.group].push(item);
  });

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={onMobileClose} />
      )}

      <aside
        className={cn(
          'fixed left-0 top-0 z-50 flex h-screen flex-col font-sans transition-[width] duration-DEFAULT ease-ease',
          'max-md:w-sidebar',
          collapsed ? 'md:w-sidebar-mini' : 'md:w-sidebar',
          mobileOpen ? 'max-md:translate-x-0' : 'max-md:-translate-x-full',
        )}
        style={{
          background: C.bg,
          color: C.text,
          paddingTop: 18,
          paddingBottom: 12,
        }}
      >
        {/* ── Brand header ───────────────────────────────────── */}
        <div
          className="flex items-center"
          style={{
            gap: 12,
            paddingLeft: collapsed ? 12 : 18,
            paddingRight: collapsed ? 12 : 14,
            justifyContent: collapsed ? 'center' : 'space-between',
            marginBottom: 18,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            <GeonomicsLogo collapsed={collapsed && !mobileOpen} />
          </div>

          {/* Desktop collapse toggle */}
          {showLabels && (
            <button
              onClick={onToggle}
              className="hidden md:flex"
              style={{
                width: 26,
                height: 26,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 5,
                color: 'rgba(255,255,255,0.45)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = C.hover;
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'rgba(255,255,255,0.45)';
              }}
              aria-label="Collapse sidebar"
            >
              <ChevronLeft size={16} />
            </button>
          )}

        </div>

        {/* Collapsed expand button */}
        {!showLabels && (
          <button
            onClick={onToggle}
            className="hidden md:flex"
            style={{
              alignSelf: 'center',
              width: 26,
              height: 26,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 5,
              color: 'rgba(255,255,255,0.45)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              marginBottom: 12,
            }}
            aria-label="Expand sidebar"
          >
            <ChevronRight size={16} />
          </button>
        )}

        {/* ── Grouped nav ────────────────────────────────────── */}
        <nav
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: collapsed ? '0 8px' : '0 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: collapsed ? 8 : 6,
          }}
        >
          {NAV_GROUP_ORDER.map((groupName) => {
            const items = grouped[groupName];
            if (!items.length) return null;
            return (
              <div key={groupName}>
                {showLabels && (
                  <div
                    style={{
                      fontSize: 10,
                      letterSpacing: 1.6,
                      textTransform: 'uppercase',
                      color: C.section,
                      fontWeight: 600,
                      fontFamily: 'var(--font-mono)',
                      padding: '10px 12px 6px',
                    }}
                  >
                    {groupName}
                  </div>
                )}
                {/* divider line when collapsed, since labels are hidden */}
                {!showLabels && (
                  <div
                    aria-hidden
                    style={{
                      height: 1,
                      margin: '8px 14px 6px',
                      background: 'rgba(255,255,255,0.06)',
                    }}
                  />
                )}
                <ul style={{ display: 'flex', flexDirection: 'column', gap: 1, listStyle: 'none', padding: 0, margin: 0 }}>
                  {items.map((item) => {
                    const Icon = item.icon ? iconMap[item.icon] : null;
                    return (
                      <li key={item.path}>
                        <NavLink
                          to={item.path}
                          end={item.path === '/dashboard'}
                          onClick={onMobileClose}
                          title={collapsed ? item.name : undefined}
                        >
                          {({ isActive }) => (
                            <div
                              style={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                padding: collapsed ? '8px 0' : '8px 12px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                borderRadius: 6,
                                fontSize: 13.5,
                                fontWeight: isActive ? 500 : 400,
                                color: isActive ? C.textActive : C.text,
                                background: isActive ? C.active : 'transparent',
                                textDecoration: 'none',
                                cursor: 'pointer',
                                transition: 'background-color 120ms, color 120ms',
                              }}
                              onMouseEnter={(e) => {
                                if (!isActive) {
                                  e.currentTarget.style.background = C.hover;
                                  e.currentTarget.style.color = C.textActive;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isActive) {
                                  e.currentTarget.style.background = 'transparent';
                                  e.currentTarget.style.color = C.text;
                                }
                              }}
                            >
                              {/* Active-indicator bar — sits at the sidebar's left edge */}
                              {isActive && (
                                <span
                                  aria-hidden
                                  style={{
                                    position: 'absolute',
                                    left: collapsed ? -8 : -12,
                                    top: 6,
                                    bottom: 6,
                                    width: 2.5,
                                    borderRadius: 2,
                                    background: C.accentBright,
                                  }}
                                />
                              )}
                              {Icon && (
                                <Icon
                                  size={17}
                                  style={{
                                    flexShrink: 0,
                                    opacity: isActive ? 1 : 0.85,
                                  }}
                                />
                              )}
                              {showLabels && (
                                <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {displayName(item.name)}
                                </span>
                              )}
                            </div>
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* ── User block + settings dropdown ──────────────────── */}
        <UserBlock
          collapsed={!showLabels}
          username={user?.username}
          role={user?.role}
          initials={initials}
          onLogout={logout}
        />
      </aside>
    </>
  );
}

// "Wells Management" → "Wells", "Companies Management" → "Companies",
// "Tool Models" stays as-is. Keeps the nav labels short like the design.
function displayName(name: string): string {
  return name.replace(/\s+Management$/i, '');
}

// ─────────────────────────────────────────────────────────────
// UserBlock — bottom-of-sidebar identity + settings menu
// ─────────────────────────────────────────────────────────────

interface UserBlockProps {
  collapsed: boolean;
  username?: string;
  role?: string;
  initials: string;
  onLogout: () => void;
}

function UserBlock({ collapsed, username, role, initials, onLogout }: UserBlockProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const goChangePassword = () => {
    setMenuOpen(false);
    navigate('/dashboard/change-password');
  };
  const doLogout = () => {
    setMenuOpen(false);
    onLogout();
  };

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'relative',
        borderTop: `1px solid ${C.divider}`,
        marginTop: 8,
        paddingTop: 10,
        padding: collapsed ? '10px 8px 0' : '10px 12px 0',
      }}
    >
      {collapsed ? (
        // Mini — just the avatar + a small settings button stacked
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '4px 0' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              background: 'linear-gradient(135deg, #15808d, #0b4a6f)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 11,
              fontWeight: 600,
            }}
            title={username ?? ''}
          >
            {initials}
          </div>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              width: 26,
              height: 26,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 5,
              background: menuOpen ? C.hover : 'transparent',
              border: 'none',
              color: menuOpen ? '#fff' : 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
            }}
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <Settings size={14} />
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              background: 'linear-gradient(135deg, #15808d, #0b4a6f)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {username ?? '—'}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{role ?? ''}</div>
          </div>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 5,
              background: menuOpen ? C.hover : 'transparent',
              border: 'none',
              color: menuOpen ? '#fff' : 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              transition: 'background-color 120ms, color 120ms',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              if (!menuOpen) {
                e.currentTarget.style.background = C.hover;
                e.currentTarget.style.color = '#fff';
              }
            }}
            onMouseLeave={(e) => {
              if (!menuOpen) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
              }
            }}
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <Settings size={15} />
          </button>
        </div>
      )}

      {/* ── Account dropdown ─────────────────────────────────── */}
      {menuOpen && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 4px)',
            left: collapsed ? 6 : 10,
            right: collapsed ? undefined : 10,
            minWidth: collapsed ? 200 : undefined,
            background: '#111b27',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
            padding: 6,
            zIndex: 60,
          }}
        >
          <MenuItem
            icon={<KeyRound size={14} />}
            label="Change password"
            onClick={goChangePassword}
          />
          <div style={{ height: 1, margin: '6px 8px', background: 'rgba(255,255,255,0.06)' }} aria-hidden />
          <MenuItem
            icon={<LogOut size={14} />}
            label="Sign out"
            onClick={doLogout}
            danger
          />
        </div>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      role="menuitem"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        background: hover
          ? danger
            ? 'rgba(180,69,59,0.18)'
            : 'rgba(255,255,255,0.06)'
          : 'transparent',
        color: danger
          ? hover
            ? '#ff9a90'
            : 'rgba(255,255,255,0.78)'
          : hover
            ? '#fff'
            : 'rgba(255,255,255,0.78)',
        border: 'none',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 500,
        fontFamily: 'var(--font-body)',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background 120ms, color 120ms',
      }}
    >
      <span style={{ display: 'inline-flex', width: 18, justifyContent: 'center', opacity: 0.9 }}>{icon}</span>
      {label}
    </button>
  );
}
